/**
 * Half-Height Testing Process
 * 
 * Spawns 4 demo views for testing half-height stacking:
 * - View 1: Regular-width half-height (size 0)
 * - View 2: Regular-width half-height (size 0)
 * - View 3: Double-width half-height (size 2)
 * - View 4: Double-width half-height (size 2)
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';

export class HalfHeightTestingProcess extends BaseProcess {
  kind: string = 'half-height-testing';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'demo' as const,
    canStop: true,
    preserveState: false,
    timeSensitive: false
  };

  private views: Map<string, HalfHeightTestView> = new Map();

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[HalfHeightTestingProcess] Starting process ${this.id}`);
    
    // Listen for spawn requests
    bus.on('half-height-testing.spawn', (payload: { viewNumber: 1 | 2 | 3 | 4 }) => {
      this.spawnView(ctx, payload.viewNumber);
    });
    
    // Auto-spawn all 4 views on start
    this.spawnView(ctx, 1);
    this.spawnView(ctx, 2);
    this.spawnView(ctx, 3);
    this.spawnView(ctx, 4);
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private spawnView(ctx: ProcessContext, viewNumber: 1 | 2 | 3 | 4): void {
    const viewId = `${this.id}-view-${viewNumber}`;
    
    // Don't spawn if already exists
    if (this.views.has(viewId)) {
      return;
    }
    
    // View 1 and 2: Regular-width half-height (size 0)
    // View 3 and 4: Double-width half-height (size 2)
    const sizes = viewNumber <= 2 ? [0] : [2];
    
    const view = new HalfHeightTestView(
      this.id,
      viewId,
      viewNumber,
      sizes,
      this
    );
    
    const viewInstance = view.toViewInstance();
    this.views.set(viewId, view);
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
    
    log.debug(`[HalfHeightTestingProcess] Spawned view ${viewId} with sizes ${sizes.join(', ')}`);
  }
}

/**
 * Half-Height Test View
 * 
 * Simple view that displays its number and size configuration.
 */
class HalfHeightTestView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private viewNumber: number,
    sizes: number[],
    private process: HalfHeightTestingProcess
  ) {
    super(processId, viewId, {
      sizes,
      showDismissButton: true
    });
  }

  protected getContentHTML(): string {
    const isDoubleWidth = this.viewNumber >= 3;
    const widthLabel = isDoubleWidth ? 'Double-Width' : 'Regular-Width';
    const sizeLabel = isDoubleWidth ? 'Size 2' : 'Size 0';
    
    return `
      <div style="
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 24px;
        box-sizing: border-box;
        background: rgba(102, 204, 255, 0.05);
        border: 1px solid rgba(102, 204, 255, 0.2);
      ">
        <div style="
          font-size: 48px;
          font-weight: bold;
          color: rgba(102, 204, 255, 0.9);
          margin-bottom: 16px;
        ">${this.viewNumber}</div>
        <div style="
          font-size: 14px;
          color: rgba(102, 204, 255, 0.7);
          text-align: center;
        ">
          <div>${widthLabel}</div>
          <div style="margin-top: 4px; opacity: 0.8;">Half-Height</div>
          <div style="margin-top: 4px; opacity: 0.6; font-size: 12px;">${sizeLabel}</div>
        </div>
      </div>
    `;
  }

  protected render(): void {
    super.render();
  }
}

registerProcess('half-height-testing', (id: string) => new HalfHeightTestingProcess(id));

